import axios, { AxiosError } from 'axios';
import { queryClient } from './queryClient';

/** The error envelope every failed request returns (AllExceptionsFilter on
 * the API side): `code` is a stable machine key, `message` is Romanian and
 * user-facing, `details` is present only for field-level validation errors. */
export interface ApiError {
  statusCode: number;
  code: string;
  message: string;
  details: { field: string; message: string }[] | null;
  path: string;
  timestamp: string;
}

/** Type guard/extractor for components: `catch (err) { const apiErr =
 * getApiError(err); ... }`. Returns null for anything that isn't the shape
 * the interceptor below throws (e.g. a bug threw something else). */
export function getApiError(err: unknown): ApiError | null {
  if (
    err !== null &&
    typeof err === 'object' &&
    'statusCode' in err &&
    'code' in err &&
    'message' in err
  ) {
    return err as ApiError;
  }
  return null;
}

// Exact paths axios hands back on `error.config.url` for the two exempt
// calls (see features/auth/api.ts) — NOT the full request URL, `baseURL` is
// combined separately at transport time and never mutates `config.url`.
// Matched exactly (after stripping any query string) rather than by
// substring, so a future endpoint like `/auth/members` can never
// accidentally inherit this exemption just because its path contains
// "/auth/me" or "/auth/login" as a substring.
const EXEMPT_PATHS = new Set(['/auth/login', '/auth/me']);

function isExemptFromHardRedirect(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0];
  // /auth/login: a wrong-password 401 must reach the LoginPage's own Alert,
  // not blow away the page the user is typing their password into.
  // /auth/me: this is the SPA's own "am I logged in" probe (useSession),
  // called both at boot and by AuthGuard/LoginRoute on every mount. An
  // anonymous visit 401s here by design — hard-redirecting on it would
  // reload straight back into another /auth/me call, looping forever.
  return EXEMPT_PATHS.has(path);
}

export const apiClient = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  // Serialize array params as REPEATED keys (`clientIds=a&clientIds=b`), not
  // axios's default bracket form (`clientIds[]=a`). The API runs Express 5,
  // whose default query parser is `'simple'` (querystring) — it reads repeated
  // keys as an array but treats `clientIds[]` as a literal key, so the bracket
  // form would silently drop the filter. `indexes: null` selects repeat mode.
  paramsSerializer: { indexes: null },
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const envelope = error.response?.data;
    const status = error.response?.status;

    if (status === 401 && !isExemptFromHardRedirect(error.config?.url)) {
      // Session cookie is gone/expired mid-use (not the boot check, not a
      // login attempt) — hard navigation clears every in-memory bit of
      // state (React Query cache, component state, everything) instead of
      // trying to reconcile a half-authenticated SPA.
      queryClient.clear();
      window.location.assign('/login');
    }

    if (envelope) {
      return Promise.reject(envelope);
    }

    // No response body at all (network down, CORS, API not running).
    const fallback: ApiError = {
      statusCode: status ?? 0,
      code: 'NETWORK_ERROR',
      message: 'Nu s-a putut contacta serverul. Verifică conexiunea.',
      details: null,
      path: error.config?.url ?? '',
      timestamp: new Date().toISOString(),
    };
    return Promise.reject(fallback);
  },
);
